import { Button, Group, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconChevronLeft } from '@tabler/icons-react'
import { useState } from 'react'
import { BookingSummary } from './BookingSummary'
import { PropertySelector } from './PropertySelector'
import { CITY_LABELS, InfoFormValues, Property } from './types'

interface InfoStepProps {
  initialFirstName: string
  initialLastName: string
  initialPhoneNumber?: string
  properties: Property[]
  city: string
  selectedDate: Date
  selectedTime: Date
  selectedEndTime: Date
  onSubmit: (values: InfoFormValues) => void
  onBack: () => void
  isSubmitting: boolean
}

export function InfoStep({
  initialFirstName,
  initialLastName,
  initialPhoneNumber,
  properties,
  city,
  selectedTime,
  selectedEndTime,
  onSubmit,
  onBack,
  isSubmitting,
}: InfoStepProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    properties.length > 0 ? String(properties[0].id) : 'other',
  )

  const showAddressFields =
    properties.length === 0 || selectedPropertyId === 'other'

  const form = useForm<InfoFormValues>({
    initialValues: {
      firstName: initialFirstName,
      lastName: initialLastName,
      phoneNumber: initialPhoneNumber || '',
      street: '',
      postalCode: '',
      propertyType: 'detached_house',
      notes: '',
    },
    validate: {
      firstName: (val) => (!val.trim() ? 'Etunimi on pakollinen' : null),
      lastName: (val) => (!val.trim() ? 'Sukunimi on pakollinen' : null),
      phoneNumber: (val) => {
        if (!val.trim()) return 'Puhelinnumero on pakollinen'
        if (!/^[\d\s+()-]{7,}$/.test(val)) {
          return 'Virheellinen puhelinnumero'
        }
        return null
      },
      street: (val) =>
        showAddressFields && !val ? 'Osoite on pakollinen' : null,
      postalCode: (val) => {
        if (!showAddressFields) return null
        if (!val) return 'Postinumero on pakollinen'
        if (!/^\d{5}$/.test(val)) return 'Postinumeron tulee olla 5 numeroa'
        return null
      },
      propertyType: (val) =>
        showAddressFields && !val ? 'Kiinteistön tyyppi on pakollinen' : null,
    },
  })

  const handleSubmit = (values: InfoFormValues) => {
    const submitValues = {
      ...values,
      propertyId:
        selectedPropertyId && selectedPropertyId !== 'other'
          ? Number(selectedPropertyId)
          : undefined,
    }
    onSubmit(submitValues)
  }

  const cityLabel = CITY_LABELS[city] || city

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <div>
          <Text size="xl" fw={600} mb="xs">
            Tarkista tiedot
          </Text>
          <Text size="sm" c="dimmed">
            Näillä tiedoilla varaamme kartoituskäynnin
          </Text>
        </div>

        <BookingSummary
          selectedTime={selectedTime}
          selectedEndTime={selectedEndTime}
          cityLabel={cityLabel}
        />

        <PropertySelector
          properties={properties}
          selectedPropertyId={selectedPropertyId}
          onPropertyChange={setSelectedPropertyId}
          showAddressFields={showAddressFields}
          form={form}
        />

        <Group grow>
          <TextInput
            label="Etunimi"
            placeholder="Matti"
            required
            autoComplete="given-name"
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label="Sukunimi"
            placeholder="Meikäläinen"
            required
            autoComplete="family-name"
            {...form.getInputProps('lastName')}
          />
        </Group>

        <TextInput
          label="Puhelinnumero"
          placeholder="050 123 4567"
          required
          autoComplete="tel"
          {...form.getInputProps('phoneNumber')}
        />

        <Textarea
          label="Lisätiedot (valinnainen)"
          placeholder="Esim. kiinteistön ikä, kunto, tai muita tietoja jotka voivat auttaa kartoituskäynnillä"
          rows={3}
          {...form.getInputProps('notes')}
        />

        <Group mt="md">
          <Button
            variant="default"
            onClick={onBack}
            disabled={isSubmitting}
            size="md"
            flex={1}
            leftSection={<IconChevronLeft size={18} />}
          >
            Takaisin
          </Button>
          <Button type="submit" loading={isSubmitting} size="md" flex={1}>
            Varaa käynti
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

export type { InfoFormValues }
